import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import { ConfirmationDialogWithIcon, WcnButton } from '@/components';
import { LogicServiceLayout, logicServiceManagementService } from '@/services/logicServiceManagementService';
import FormContainer, { FormContainerRef } from '@/components/form/FormContainer.tsx';
import { FormTextField } from '@/components/form/FormTextField';
import { required } from '@core/utils/validation';
import { MAX_FILE_SIZE_BYTES } from '@core/constants/fileSizes';
import { ConfirmationDialogRef } from '@/components/dialog/ConfirmationDialogWithIcon.tsx';

const NAME_MAX = 100;
const CHANGES_MAX = 500;

interface LogicServiceEditorProps {
    mode: 'add' | 'edit';
    layout?: LogicServiceLayout | null;
    onSave: (data: { name: string; configJson: string; description: string }) => void | Promise<void>;
    onCancel: () => void;
    isSaving?: boolean;
}

const LogicServiceEditor = ({ mode, layout, onSave, onCancel, isSaving = false }: LogicServiceEditorProps) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dropZoneRef = useRef<HTMLDivElement>(null);
    const formRef = useRef<FormContainerRef>(null);
    const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const cancelledRef = useRef(false);

    const [layoutName, setLayoutName] = useState('');
    const [changes, setChanges] = useState('');
    const [configJson, setConfigJson] = useState('');
    const [fileName, setFileName] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [hasFileError, setHasFileError] = useState(false);
    const [fileErrorText, setFileErrorText] = useState('');
    const [formValid, setFormValid] = useState(false);
    const [existingFileCleared, setExistingFileCleared] = useState(false);

    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadingFileName, setUploadingFileName] = useState('');

    const confirmDialogRef = useRef<ConfirmationDialogRef>(null);
    const [pendingSaveData, setPendingSaveData] = useState<{
        name: string;
        configJson: string;
        description: string;
    } | null>(null);

    const clearProgressInterval = useCallback(() => {
        if (progressIntervalRef.current !== null) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
        }
    }, []);

    const resetFileState = useCallback(() => {
        setIsUploading(false);
        setUploadProgress(0);
        setUploadingFileName('');
        setFileName('');
        setConfigJson('');
        setHasFileError(false);
        setFileErrorText('');
        clearProgressInterval();
    }, [clearProgressInterval]);

    useEffect(() => {
        setIsUploading(false);
        setUploadProgress(0);
        setUploadingFileName('');
        setFileName('');
        setHasFileError(false);
        setFileErrorText('');
        clearProgressInterval();
        setExistingFileCleared(false);
        formRef.current?.resetValidation();

        if (mode === 'edit' && layout) {
            setLayoutName(layout.name);
            setChanges('');
            setConfigJson(layout.configJson);
        } else {
            setLayoutName('');
            setChanges('');
            setConfigJson('');
        }
    }, [mode, layout?.id, clearProgressInterval]);

    useEffect(() => {
        return () => clearProgressInterval();
    }, [clearProgressInterval]);

    const handleCancelUpload = useCallback(() => {
        cancelledRef.current = true;
        clearProgressInterval();
        resetFileState();
        if (mode === 'edit' && layout) {
            setConfigJson(layout.configJson);
        }
    }, [clearProgressInterval, resetFileState, mode, layout]);

    const handleClearFile = useCallback(() => {
        resetFileState();
        setExistingFileCleared(true);
    }, [resetFileState]);

    const processFile = useCallback(
        async (file: File) => {
            cancelledRef.current = false;

            setIsUploading(true);
            setUploadProgress(0);
            setUploadingFileName(file.name);
            setHasFileError(false);
            setFileErrorText('');
            setConfigJson('');
            setFileName('');

            clearProgressInterval();
            progressIntervalRef.current = setInterval(() => {
                setUploadProgress((prev) => {
                    if (prev >= 90) {
                        clearProgressInterval();
                        return 90;
                    }
                    return prev + 6;
                });
            }, 30);

            await new Promise((r) => setTimeout(r, 50));
            if (cancelledRef.current) return;

            if (!file.name.endsWith('.json') && file.type !== 'application/json') {
                clearProgressInterval();
                setUploadProgress(100);
                await new Promise((r) => setTimeout(r, 200));
                if (cancelledRef.current) return;
                setIsUploading(false);
                setUploadProgress(0);
                setUploadingFileName('');
                setHasFileError(true);
                setFileErrorText(t('invalidJsonFormat'));
                setFileName(file.name);
                setConfigJson('');
                return;
            }

            if (file.size > MAX_FILE_SIZE_BYTES) {
                clearProgressInterval();
                setUploadProgress(100);
                await new Promise((r) => setTimeout(r, 200));
                if (cancelledRef.current) return;
                setIsUploading(false);
                setUploadProgress(0);
                setUploadingFileName('');
                setHasFileError(true);
                setFileErrorText(t('validation.fileSizeExceeded200KB'));
                setFileName(file.name);
                setConfigJson('');
                return;
            }

            try {
                const text = await file.text();
                JSON.parse(text);

                clearProgressInterval();
                setUploadProgress(100);
                await new Promise((r) => setTimeout(r, 300));
                if (cancelledRef.current) return;

                setIsUploading(false);
                setUploadProgress(0);
                setUploadingFileName('');
                setConfigJson(text);
                setFileName(file.name);
                setHasFileError(false);
                setFileErrorText('');
            } catch {
                clearProgressInterval();
                setUploadProgress(100);
                await new Promise((r) => setTimeout(r, 200));
                if (cancelledRef.current) return;
                setIsUploading(false);
                setUploadProgress(0);
                setUploadingFileName('');
                setHasFileError(true);
                setFileErrorText(t('invalidJsonFormat'));
                setFileName(file.name);
                setConfigJson('');
            }
        },
        [t, clearProgressInterval]
    );

    const handleFileChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;
            e.target.value = '';
            await processFile(file);
        },
        [processFile]
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        if (!dropZoneRef.current?.contains(e.relatedTarget as Node)) {
            setIsDragging(false);
        }
    }, []);

    const handleDrop = useCallback(
        async (e: React.DragEvent) => {
            e.preventDefault();
            setIsDragging(false);
            const file = e.dataTransfer.files?.[0];
            if (!file) return;
            await processFile(file);
        },
        [processFile]
    );

    const handleSave = useCallback(() => {
        const isFormValid = formRef.current?.validate() ?? false;
        const hasConfig = mode === 'edit' ? !!configJson : !!configJson && !hasFileError;

        if (!isFormValid || !hasConfig) {
            toast(
                <div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>{t('saveFailed')}</div>
                    <div style={{ fontSize: '12px', opacity: 0.9 }}>{t('pleaseFixFormErrors')}</div>
                </div>,
                {
                    type: 'error',
                    position: 'bottom-center',
                    autoClose: 5000,
                    closeOnClick: true,
                    pauseOnHover: true,
                    icon: false
                }
            );
            return;
        }

        const saveData = { name: layoutName.trim(), configJson, description: changes };

        if (mode === 'edit' && layout && layout.assignedShops.length > 0) {
            setPendingSaveData(saveData);
            confirmDialogRef.current?.openDialog();
        } else {
            onSave(saveData);
        }
    }, [layoutName, configJson, changes, onSave, mode, layout, hasFileError, t]);

    const handleConfirmSave = useCallback(async () => {
        if (pendingSaveData) {
            await onSave(pendingSaveData);
            setPendingSaveData(null);
        }
    }, [pendingSaveData, onSave]);

    const handleCancelConfirm = useCallback(() => {
        if (isSaving) return;
        setPendingSaveData(null);
    }, [isSaving]);

    const title = mode === 'add' ? t('addLayout') : t('editLayout');

    // In edit mode with no new file uploaded yet — show existing layout file box
    const showEditExistingState = mode === 'edit' && !fileName && !isUploading && !existingFileCleared;
    const isSuccess = !!configJson && !!fileName && !hasFileError;
    const isError = hasFileError && !!fileName;

    // Show file box when: uploading, new file chosen, OR edit mode showing existing file
    const showFileBox = isUploading || !!fileName || showEditExistingState;

    const displayFileName = isUploading
        ? uploadingFileName
        : fileName || (showEditExistingState && layout ? `${layout.name}_v${layout.version}.json` : '');

    const fileBoxBorderColor = isError
        ? 'var(--color-field-error)'
        : isSuccess
          ? 'var(--color-success)'
          : showEditExistingState
            ? 'var(--main-blue)'
            : 'var(--sidebar-divider)';

    const canSave =
        layoutName.trim().length > 0 && changes.trim().length > 0 && (mode === 'edit' ? !!configJson : isSuccess);

    return (
        <>
            <Box
                className="logic-service-editor"
                sx={{
                    border: '1px solid var(--sidebar-divider)',
                    borderRadius: '3px',
                    backgroundColor: 'background.paper',
                    mt: 3
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 1.5 }}>
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 400,
                            letterSpacing: '0.05em',
                            fontFamily: 'var(--font-family-primary)',
                            textTransform: 'uppercase'
                        }}
                    >
                        {title}
                    </Typography>
                </Box>

                <FormContainer formRef={formRef} onChange={setFormValid}>
                    <Box sx={{ px: 3, py: 6, display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
                        {mode === 'edit' && layout && (
                            <Box sx={{ width: '66.666%' }}>
                                <Typography
                                    variant="caption"
                                    className="logic-service-version-label"
                                    sx={{
                                        display: 'block',
                                        mb: 0.5,
                                        pl: 1.25,
                                        ml: 0.25,
                                        color: 'var(--gray-medium)',
                                        fontSize: '12px'
                                    }}
                                >
                                    {t('versionLastChanged')}:
                                </Typography>
                                <Box
                                    className="logic-service-version-box"
                                    sx={{
                                        border: '1px solid var(--sidebar-divider)',
                                        borderRadius: '3px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        px: 1.5,
                                        backgroundColor: 'action.hover'
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            fontFamily: 'var(--font-family-medium)',
                                            fontStyle: 'italic',
                                            color: 'var(--gray-medium)'
                                        }}
                                    >
                                        {DateTime.fromISO(layout.uploadDateTime).toLocal().toFormat('dd.MM.yyyy HH:mm')}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        <Box sx={{ width: '66.666%' }}>
                            <FormTextField
                                name="layoutName"
                                label={t('name')}
                                value={layoutName}
                                onChange={(e) => setLayoutName(e.target.value.slice(0, NAME_MAX))}
                                fullWidth
                                size="small"
                                required
                                rules={[required(t)]}
                                inputProps={{ maxLength: NAME_MAX }}
                                sx={{
                                    '& .MuiOutlinedInput-root': { borderRadius: '3px' },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--sidebar-divider)' }
                                }}
                            />
                            {mode === 'edit' && layout && (
                                <Typography
                                    variant="caption"
                                    className="logic-service-original-name"
                                    sx={{
                                        display: 'block',
                                        mt: 0.5,
                                        ml: 1,
                                        color: 'var(--gray-medium)',
                                        fontSize: '12px'
                                    }}
                                >
                                    ({t('originalName')}: {layout.name})
                                </Typography>
                            )}
                        </Box>

                        <Box sx={{ width: '66.666%' }}>
                            <FormTextField
                                name="changes"
                                label={t('changes')}
                                value={changes}
                                onChange={(e) => setChanges(e.target.value.slice(0, CHANGES_MAX))}
                                fullWidth
                                size="small"
                                required
                                rules={[required(t)]}
                                inputProps={{ maxLength: CHANGES_MAX }}
                                sx={{
                                    '& .MuiOutlinedInput-root': { borderRadius: '3px' },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--sidebar-divider)' },
                                    mb: 2.5
                                }}
                            />
                        </Box>

                        <Box
                            ref={dropZoneRef}
                            className="logic-service-dropzone"
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{
                                width: '66.666%',
                                border: '1px solid',
                                borderColor: isDragging ? 'var(--primary)' : 'var(--sidebar-divider)',
                                borderRadius: '3px',
                                px: 3,
                                py: 4,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                cursor: 'pointer',
                                backgroundColor: isDragging ? 'rgba(0,153,204,0.04)' : 'transparent',
                                transition: 'border-color 0.2s ease, background-color 0.2s ease',
                                '&:hover': { borderColor: 'var(--primary)' }
                            }}
                        >
                            <Box
                                sx={{
                                    '& i': {
                                        fontSize: '24px !important',
                                        color: `${isDragging ? 'var(--primary)' : 'var(--gray-medium)'} !important`
                                    }
                                }}
                            >
                                <i className="bi bi-upload" />
                            </Box>
                            {showEditExistingState ? (
                                <>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--gray-medium)' }}>
                                        {t('currentConfigLoaded')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'var(--gray-medium)' }}>
                                        {t('clickOrDragToReplace')}
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--gray-medium)' }}>
                                        {t('dragFilesToUpload')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'var(--gray-medium)' }}>
                                        {t('jsonFileMax200KB')}
                                    </Typography>
                                </>
                            )}
                        </Box>

                        {showFileBox && (
                            <Box sx={{ width: '66.666%' }}>
                                <Typography
                                    variant="caption"
                                    className="logic-service-file-box-label"
                                    sx={{
                                        display: 'block',
                                        mb: 0.5,
                                        ml: 0.25,
                                        color: 'var(--gray-medium)',
                                        fontSize: '12px'
                                    }}
                                >
                                    {isUploading ? t('uploadingFile') : t('uploadedLayout')}
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box
                                        className={[
                                            'logic-service-file-box',
                                            isSuccess ? 'logic-service-file-box--success' : '',
                                            isError ? 'logic-service-file-box--error' : '',
                                            showEditExistingState ? 'logic-service-file-box--existing' : ''
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        sx={{
                                            position: 'relative',
                                            flex: 1,
                                            border: '1px solid',
                                            borderColor: fileBoxBorderColor,
                                            borderRadius: '3px',
                                            height: '40px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            px: 1.5,
                                            overflow: 'hidden',
                                            transition: 'border-color 0.2s ease'
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                fontFamily: 'var(--font-family-medium)',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                flex: 1
                                            }}
                                        >
                                            {displayFileName}
                                        </Typography>

                                        {isUploading && (
                                            <Box
                                                className="logic-service-file-box-progress"
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    left: 0,
                                                    height: '4px',
                                                    width: `${uploadProgress}%`,
                                                    backgroundColor: 'var(--main-blue)',
                                                    borderRadius: '0 0 3px 3px',
                                                    transition: 'width 0.1s linear'
                                                }}
                                            />
                                        )}
                                    </Box>

                                    {isUploading ? (
                                        <IconButton
                                            size="small"
                                            onClick={handleCancelUpload}
                                            title={t('cancel')}
                                            className="logic-service-file-box-action"
                                            sx={{
                                                '& i': {
                                                    fontSize: '16px !important',
                                                    color: 'var(--gray-medium) !important'
                                                }
                                            }}
                                        >
                                            <i className="bi bi-x-circle-fill" />
                                        </IconButton>
                                    ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {showEditExistingState && layout && (
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        logicServiceManagementService.downloadLayout(layout);
                                                    }}
                                                    title={t('download')}
                                                    className="logic-service-file-box-action"
                                                    sx={{
                                                        '& i': {
                                                            fontSize: '16px !important',
                                                            color: 'var(--gray-medium) !important'
                                                        }
                                                    }}
                                                >
                                                    <i className="bi bi-download" />
                                                </IconButton>
                                            )}
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleClearFile();
                                                }}
                                                title={t('remove')}
                                                className="logic-service-file-box-action"
                                                sx={{
                                                    '& i': {
                                                        fontSize: '16px !important',
                                                        color: 'var(--gray-medium) !important'
                                                    }
                                                }}
                                            >
                                                <i className="bi bi-trash3" />
                                            </IconButton>
                                        </Box>
                                    )}
                                </Box>

                                {isError && fileErrorText && (
                                    <Typography
                                        variant="caption"
                                        className="logic-service-file-box-error-text"
                                        sx={{
                                            display: 'block',
                                            mt: 0.5,
                                            ml: 0.25,
                                            color: 'var(--color-field-error)',
                                            fontSize: '12px'
                                        }}
                                    >
                                        {fileErrorText}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".json,application/json"
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />
                    </Box>
                </FormContainer>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, px: 3, py: 3 }}>
                    <WcnButton variant="red" onClick={onCancel} disabled={isSaving}>
                        {t('cancel')}
                    </WcnButton>
                    <WcnButton variant="primary" onClick={handleSave} disabled={!canSave || isSaving}>
                        {isSaving ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <CircularProgress size={14} sx={{ color: '#ffffff' }} />
                                <span>{t('saving')}</span>
                            </Box>
                        ) : (
                            t('save')
                        )}
                    </WcnButton>
                </Box>
            </Box>

            <ConfirmationDialogWithIcon
                dialogRef={confirmDialogRef}
                heading={t('confirmEditLayout')}
                message={t('confirmEditLayoutMessage')}
                onConfirm={handleConfirmSave}
                onClose={handleCancelConfirm}
                cancelText="noCancel"
                icon={<i className="bi bi-shuffle" />}
                isSaving={isSaving}
                waitForConfirm={true}
            />
        </>
    );
};

export default LogicServiceEditor;
